import { useState } from 'react';
import { MdOutlineImage } from 'react-icons/md';

import { Button } from '@/components/Button';
import { FilePicker } from '@/components/FilePicker/FilePicker';
import Modal from '@/components/Modal/Modal';
import { uploadVesselGalleryImage } from '@/store/events/events.api';
import { useGalleryUploadEventsQuery } from '@/store/gallery/gallery.store';
import { toast } from '@/utils/toast';
import {
  isVideoAttachment,
  rememberVideoDuration,
  validateVideoFile,
  VIDEO_ACCEPT,
} from '@/utils/video-attachments';

import s from './AddToGalleryModal.module.scss';
import { EventSelect } from './EventSelect';

interface AddToGalleryModalProps {
  open: boolean;
  onClose: () => void;
  onUploaded?: () => void;
  /** Photos already known (chat attachments) — hides the file picker. */
  files?: File[];
  /**
   * The event the photo goes into, already decided by where the modal was opened
   * from — an event's own gallery. Hides the event picker.
   */
  eventId?: string;
  /** `personal` keeps the photo in the uploader's own gallery rather than sharing it. */
  scope?: 'personal';
}

/**
 * What is being added, named for what it is: "Photo", "Videos", or "Photo and
 * video" — a toast that calls a clip a photo reads as the upload having gone
 * wrong. Capitalised to lead a sentence, or not, to sit inside one.
 */
const uploadedNoun = (uploaded: File[], capitalised = false) => {
  const videos = uploaded.filter((file) => isVideoAttachment(file.type, file.name)).length;
  const photos = uploaded.length - videos;
  const noun = (count: number, one: string) => (count > 1 ? `${one}s` : one);
  const words =
    videos === 0
      ? noun(photos, 'photo')
      : photos === 0
        ? noun(videos, 'video')
        : `${noun(photos, 'photo')} and ${noun(videos, 'video')}`;
  return capitalised ? words.charAt(0).toUpperCase() + words.slice(1) : words;
};

export function AddToGalleryModal({
  open,
  onClose,
  onUploaded,
  files,
  eventId: presetEventId,
  scope,
}: AddToGalleryModalProps) {
  /* Only the events a photo can go into now — the reader's own, not yet ended —
     rather than the calendar's list, which offers an owner every event on the
     vessel and ended ones too, and the service would refuse the upload. Not asked
     for when the event is already decided by where the modal was opened. */
  const { data: events = [] } = useGalleryUploadEventsQuery(open && !presetEventId);

  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [checkingVideo, setCheckingVideo] = useState(false);

  /* A video is held to the same limits as one sent in chat by the same check
     (`validateVideoFile`), so a clip that chat takes the gallery takes
     too, and one it turns away is turned away here with the same reason. A photo
     needs no check; anything else is neither and is refused, since `accept` is
     only a hint the system picker may ignore. */
  const pickFile = async (picked: File) => {
    if (isVideoAttachment(picked.type, picked.name)) {
      setCheckingVideo(true);
      try {
        const check = await validateVideoFile(picked);
        if (!check.ok) {
          toast.error(check.reason);
          return;
        }
        rememberVideoDuration(picked.name, picked.size, check.duration);
        setPickedFile(picked);
      } finally {
        setCheckingVideo(false);
      }
      return;
    }

    if (!picked.type.startsWith('image/')) {
      toast.error('Couldn’t add this file. Please choose a photo or video.');
      return;
    }

    setPickedFile(picked);
  };

  const presetFiles = files ?? [];
  const hasPresetFiles = presetFiles.length > 0;
  const uploadFiles = hasPresetFiles ? presetFiles : pickedFile ? [pickedFile] : [];

  const selectedEventTitle = events.find((event) => event.id === eventId)?.title ?? null;

  const eventOptions = events.map((event) => ({
    label: event.title || 'Untitled event',
    value: event.id,
  }));

  const reset = () => {
    setPickedFile(null);
    setEventId(null);
  };

  const handleClose = () => {
    if (isUploading) return;
    reset();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (uploadFiles.length === 0 || isUploading) return;

    setIsUploading(true);
    try {
      await Promise.all(
        // No name of its own: the service names a memory after its file, and the
        // gallery shows the picture rather than a caption.
        uploadFiles.map((file) =>
          uploadVesselGalleryImage(file, undefined, presetEventId ?? eventId ?? undefined, scope),
        ),
      );
      toast.success(`${uploadedNoun(uploadFiles, true)} added to gallery`);
      reset();
      onUploaded?.();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Couldn’t add the ${uploadedNoun(uploadFiles)}. Please try again.`,
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Add to Gallery">
      <form className={s.form} onSubmit={handleSubmit}>
        {/* Chat attachments arrive already chosen, so there is nothing to pick —
            only what is being added. Otherwise the Library's own file picker. */}
        {hasPresetFiles ? (
          <div className={s.picked}>
            <MdOutlineImage size={20} />
            <span>{presetFiles.map((file) => file.name).join(', ')}</span>
          </div>
        ) : (
          <FilePicker
            file={pickedFile}
            placeholder={checkingVideo ? 'Checking video…' : 'Choose a photo or video'}
            accept={`image/*,${VIDEO_ACCEPT}`}
            loading={isUploading || checkingVideo}
            onPick={(picked) => void pickFile(picked)}
          />
        )}

        {!presetEventId && (
          <EventSelect
            label="Event (optional)"
            placeholder="Search event…"
            options={eventOptions}
            selectedLabel={selectedEventTitle}
            onSelect={setEventId}
          />
        )}

        <Button type="submit" disabled={uploadFiles.length === 0 || isUploading || checkingVideo}>
          {isUploading ? 'Adding…' : 'Add to Gallery'}
        </Button>
      </form>
    </Modal>
  );
}
